import { networkIconFor } from '@alison/ui';
import ArticleOutlined from '@mui/icons-material/ArticleOutlined';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import { ELLIPSIS_SX } from '../creatives/utils.js';
import { iconFor } from '../workflows/workflowIcons.js';
import { BriefSourceIcon, KIND_ICON, OpenSourceIcon, SOURCE_ICON } from './OutcomeIcons.js';
import type { Outcome } from './outcomes.js';

const CARD_SX = {
  gap: 2,
  p: 3,
  height: '100%',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  bgcolor: 'background.paper',
};

const SOURCE_SX = {
  gap: 1,
  px: 1.5,
  py: 0.75,
  alignItems: 'center',
  border: 0,
  borderRadius: 1,
  cursor: 'pointer',
  color: 'text.muted',
  bgcolor: 'background.platform',
  transition: 'background-color .15s ease, color .15s ease',
  '&:hover': { color: 'primary.main', bgcolor: 'action.selected' },
  '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
};

const SUMMARY_SX = {
  flex: 1,
  color: 'text.secondary',
  maxHeight: 200,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  WebkitLineClamp: 11,
};

const GLYPH_SX = {
  width: 34,
  height: 34,
  borderRadius: 1,
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

export interface OutcomeCardProps {
  outcome: Outcome;
  platforms: string[];
  brand: string | null;
  when: string;
  onOpen?: () => void;
  onOpenSource?: (source: Outcome['sources'][number]) => void;
}

export function OutcomeCard({
  outcome,
  platforms,
  brand,
  when,
  onOpen,
  onOpenSource,
}: OutcomeCardProps): ReactNode {
  const Glyph = iconFor(outcome.icon ?? '', KIND_ICON[outcome.kind] ?? ArticleOutlined);

  return (
    <Stack
      data-testid={`outcome-${outcome.runId}-${outcome.kind}`}
      sx={[CARD_SX, onOpen !== undefined && { cursor: 'pointer' }]}
      {...(onOpen === undefined
        ? {}
        : {
            onClick: onOpen,
            onKeyDown: (event: React.KeyboardEvent<HTMLDivElement>) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onOpen();
              }
            },
            role: 'button',
            tabIndex: 0,
            'aria-label': outcome.title,
          })}
    >
      <Stack direction="row" sx={{ gap: 2, alignItems: 'flex-start' }}>
        <Stack sx={{ ...GLYPH_SX, bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1) }}>
          <Glyph sx={{ fontSize: 18, color: 'primary.main' }} />
        </Stack>
        <Stack sx={{ gap: 0.25, minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 700, ...ELLIPSIS_SX }}>
            {outcome.title}
          </Typography>
          <Typography sx={{ color: 'text.muted' }}>
            {[outcome.creator, when].filter((part) => part !== null).join(' · ')}
          </Typography>
        </Stack>
      </Stack>

      {outcome.summary === null ? null : (
        <Tooltip placement="top-start" title={outcome.summary}>
          <Typography sx={SUMMARY_SX}>{outcome.summary}</Typography>
        </Tooltip>
      )}

      {outcome.sources.length === 0 ? null : (
        <Stack sx={{ gap: 1 }}>
          <Typography sx={{ color: 'text.muted' }}>Sources:</Typography>
          <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
            {outcome.sources.map((source) => {
              const SourceGlyph = SOURCE_ICON[source.kind] ?? BriefSourceIcon;
              return (
                <Stack
                  key={source.runId}
                  direction="row"
                  component="button"
                  type="button"
                  aria-label={`Open the ${source.kind} this was built from`}
                  onClick={(event) => {
                    event.stopPropagation();
                    onOpenSource?.(source);
                  }}
                  sx={SOURCE_SX}
                >
                  <SourceGlyph sx={{ fontSize: 14 }} />
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                    {source.kind.toUpperCase()}
                  </Typography>
                  <OpenSourceIcon sx={{ fontSize: 12, color: 'primary.main' }} />
                </Stack>
              );
            })}
          </Stack>
        </Stack>
      )}

      <Stack direction="row" sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography sx={{ color: 'text.muted' }}>{`Workflow: ${outcome.workflow}`}</Typography>
        {brand === null ? null : (
          <Typography sx={{ color: 'text.muted' }}>{`· ${brand}`}</Typography>
        )}
        {platforms.map((slug) => {
          const Icon = networkIconFor(slug);
          return Icon === undefined ? null : (
            <Box key={slug} sx={{ display: 'inline-flex' }}>
              <Icon titleAccess={slug} sx={{ fontSize: 14 }} />
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
}
